<script lang="ts" setup>
import { onMounted, ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/store/user'
import { usePayrollStore } from '@/store/payroll-data'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { notificationError } from '@/app/core/util/notificationUtils'
import { useI18n } from 'vue-i18n'
import CodeChange from '@/app/features/employees/components/CodeChange.vue'
import Popper from 'vue3-popper'
import {
  DBtn,
  DTextField,
  DTable,
  DModal,
  DLoading,
  DToggleField,
  SearchMdIcon,
  Edit03Icon,
  Trash03Icon,
  AlertTriangleIcon,
  Loading02Icon,
  UsersEditIcon,
  RouteIcon
} from 'front-library'
import {
  generateReportList,
  getReportRequest
} from '@/app/core/services/reports'
import {
  getEmployees,
  Employee,
  deleteEmployee,
  checkPendingEmployeeCodeChangeRequests,
  getEmployeeReentries
} from '@/app/core/services/employees'
import EmployeeReentryModal from '@/app/features/employees/components/EmployeeReentryModal.vue'
usePayrollChange(() => window.location.reload())

/* store, router, notifications */
const router = useRouter()
const store = useUserStore()
const storePayroll = usePayrollStore()
const notifications = useNotificationsStore()
const { t } = useI18n()

/* Datos componente Modal y Loading */
const showModal = ref(false)
const showModalCodeRequest = ref(false)
const showModalReentry = ref(false)

const titleModal = ref('')
const descriptionModal = ref('')
const action = ref('')
const typeAlertModal = ref('')
const loading = ref(false)
const messageLoading = ref('')

/* Datos */
const search = ref()
const includeInactive = ref(false)
const tableEmployee = ref(null)
const employeeForModify = ref()
const employeeForReentry = ref()
const urlFile = ref(null)
const refresh = ref()
const payload = ref()
const hasRequests = ref(false)
const employeesWithReentry = ref<Set<string>>(new Set())

/* Función para activar toggle incluir inactivos */
const activateIncludeInactive = () => {
  includeInactive.value = true
  employees()
}

/* Datos componente table */
const columns = ref([
  {
    name: 'code_employee',
    title: t('employees.list.code'),
    table: 'code_employee'
  },
  {
    name: 'name_employee',
    title: t('employees.list.name'),
    table: 'name_employee'
  },
  {
    name: 'identification_number',
    title: t('employees.list.identificationNumber'),
    table: 'identification_number'
  },
  {
    name: 'joining_date',
    title: t('employees.list.joiningDate'),
    table: 'joining_date'
  },
  {
    name: 'leave_date',
    title: t('employees.list.leaveDate'),
    table: 'leave_date'
  },
  {
    name: 'department',
    title: t('employees.list.department'),
    table: 'department'
  },
  { name: 'sucursal', title: t('employees.list.sucursal'), table: 'sucursal' },
  { name: 'position', title: t('employees.list.position'), table: 'position' },
  { name: 'journey', title: t('employees.list.journey'), table: 'journey' },
  { name: 'turn', title: t('employees.list.turn'), table: 'turn' },
  {
    name: 'recognition_date',
    title: t('employees.list.recognitionDate'),
    table: 'recognition_date'
  },
  {
    name: 'updated_at',
    title: t('employees.list.lastUpdate'),
    table: 'updated_at'
  },
  ...(storePayroll.payroll?.country.iso_code == 'MX'
    ? [
        {
          name: 'family_medicine_unit',
          title: t('employees.list.familyMedicineUnit'),
          table: 'family_medicine_unit'
        },
        {
          name: 'type_salary',
          title: t('employees.list.typeSalary'),
          table: 'type_salary'
        },
        {
          name: 'sua_workday',
          title: t('employees.list.suaWorkday'),
          table: 'sua_workday'
        },
        {
          name: 'salary_assimilation',
          title: t('employees.list.salaryAssimilation'),
          table: 'salary_assimilation'
        },
        {
          name: 'colony',
          title: t('employees.list.colony'),
          table: 'colony'
        },
        {
          name: 'sua_contract_type',
          title: t('employees.list.suaContractType'),
          table: 'sua_contract_type'
        },
        {
          name: 'sat_workday_type',
          title: t('employees.list.satWorkdayType'),
          table: 'sat_workday_type'
        }
      ]
    : []),
  { name: 'actions', title: '' }
])
const rows = ref<Employee[]>([])
const perPage = ref(0)
const offset = ref(0)
const totalRows = ref(0)
const sortBy = ref('')

/* Redirect's */
const createEmployee = () => {
  void router.push({ name: 'CreateEmployee' })
}
const updateEmployee = (id: any) => {
  void router.push({ name: 'UpdateEmployee', params: { id } })
}
const getEmployee = (id: any) => {
  void router.push({ name: 'GetEmployee', params: { id } })
}

/* Eliminar empleado */
const removeEntity = (values: any) => {
  titleModal.value = t('employees.list.actionStatus', {
    status: t('table.action.delete'),
    employee: values.name_employee as string
  })
  descriptionModal.value = t('modal.description')
  action.value = t('table.action.delete')
  typeAlertModal.value = 'question'
  showModal.value = true
  employeeForModify.value = values.id
}

/* Solicitud de cambio de código de empleado */
const changeCodeRequest = (values: any) => {
  showModalCodeRequest.value = true
  hasRequests.value = values.codeChange
  employeeForModify.value = values.id
}

/* Solicitud de reingreso de empleado */
const reentryRequest = (values: any) => {
  showModalReentry.value = true
  employeeForReentry.value = values
}

const deleteConfirm = async () => {
  loading.value = true
  messageLoading.value = ''
  showModal.value = false
  await deleteEmployee(employeeForModify.value)
    .then(response => {
      notifications.addNotification({
        title: t('notification.removed'),
        text: t('employees.notification.action', [
          t('notification.wasRemoved')
        ]),
        type: 'success',
        time: 10000
      })
      void employees()
    })
    .catch(async (error: any) => {
      notificationError(error, 'employees', t, notifications, loading)
    })
}

/* Generar reporte */
const downloadList = async () => {
  try {
    loading.value = true
    messageLoading.value = t('loading.report')
    urlFile.value = null

    const data = {
      code: 'configurations/entity-table',
      parameters: {
        query: JSON.stringify(payload.value),
        fields: payload.value.fields.filter(
          (field: any) =>
            field !== 'employees.id' && !field.includes('codigo_name')
        ),
        headers: columns.value
          .filter((item: any) => item.title !== '')
          .map((item: any) => item.title),
        module: t('employees.list.title', 1)
      }
    }
    const id = await generateReportList(data)
    refresh.value = setInterval(async () => {
      const response = await getReportRequest(id)
      if (response.status === 'Completado') {
        urlFile.value = response.url
        descriptionModal.value = ''
        typeAlertModal.value = 'success'
        titleModal.value = t('reports.notification.reportGenerated')
        showModal.value = true
        loading.value = false
        clearInterval(refresh.value)
      } else if (response.status === 'Error') {
        typeAlertModal.value = 'error'
        titleModal.value = t('reports.notification.reportNoGenerated')
        descriptionModal.value = response.errors
        showModal.value = true
        loading.value = false
        clearInterval(refresh.value)
      }
    }, 5000)
  } catch (error: any) {
    notificationError(error, 'employees', t, notifications, loading)
  }
}

/* Cerrar el componente Modal */
const closeModal = () => {
  showModal.value = false
  showModalCodeRequest.value = false
  showModalReentry.value = false
  urlFile.value = null
}

/* Manejar reingreso exitoso */
const handleReentrySuccess = () => {
  showModalReentry.value = false
  void employeeReentries()
}

const employees = async () => {
  loading.value = true
  messageLoading.value = t('loading.data')
  try {
    const data: any = await getEmployees(
      offset.value,
      perPage.value,
      sortBy.value,
      search.value === '' ? undefined : search.value,
      storePayroll.payroll?.country.iso_code,
      includeInactive.value
    )
    rows.value = data[0].results
    totalRows.value = data[0].totalItems
    payload.value = data[1]

    await employeeCodeChangeRequests()
    await employeeReentries()
    loading.value = false
  } catch (error: any) {
    notificationError(error, 'employees', t, notifications, loading)
  }
}

const modifyHasRequests = () => {
  hasRequests.value = !hasRequests.value
}

const employeeCodeChangeRequests = async () => {
  try {
    const data: any = await checkPendingEmployeeCodeChangeRequests(
      storePayroll.payroll?.id ?? ''
    )

    data[0].results.forEach((request: any) => {
      let employee = rows.value.find(
        (row: any) => row.id === request.employee_id
      )
      if (employee) {
        employee.codeChange = request.request_status_code
      }
    })
  } catch (error: any) {
    notificationError(error, 'employees', t, notifications, loading)
  }
}

const employeeReentries = async () => {
  try {
    const data: any = await getEmployeeReentries(storePayroll.payroll?.id ?? '')
    employeesWithReentry.value.clear()

    if (data && data.length > 0) {
      data.forEach((reentry: any) => {
        employeesWithReentry.value.add(reentry.employee_id)
      })
    }
  } catch (error: any) {
    notificationError(error, 'employees', t, notifications, loading)
  }
}

onMounted(async () => {
  try {
    if (store.hasPermission('employee_code_change_request:create')) {
      setInterval(() => {
        void employeeCodeChangeRequests()
      }, 5000)
    }
  } catch (error: any) {
    notificationError(error, 'employees', t, notifications, loading)
  }
})
</script>
<template>
  <DLoading v-show="loading" :message="messageLoading" />

  <DModal
    :open="showModal"
    :typeAlert="typeAlertModal"
    :title="titleModal"
    :description="descriptionModal"
    :acceptQuestion="t('modal.acceptQuestion', { action })"
    @closeModal="closeModal"
    @otherMethod="deleteConfirm">
    <template v-slot:description v-if="urlFile != null">
      <a
        class="underline tracking-widest text-blue-600"
        :href="urlFile"
        target="_blank"
        v-html="t('reports.notification.viewFile')" />
    </template>
  </DModal>

  <CodeChange
    v-if="showModalCodeRequest"
    :employeeForModify="employeeForModify"
    :hasRequests="hasRequests"
    :showModal="showModalCodeRequest"
    @modify:hasRequests="modifyHasRequests"
    @get:employees="employees"
    @close:modal="closeModal" />

  <EmployeeReentryModal
    v-show="showModalReentry"
    :open="showModalReentry"
    :employee-id="employeeForReentry?.id"
    :employee-code="employeeForReentry?.code_employee"
    :employee-name="employeeForReentry?.name_employee"
    :recognition-date="employeeForReentry?.recognition_date"
    @close="closeModal"
    @success="handleReentrySuccess" />

  <div class="h-full px-5 overflow-auto scroll">
    <div class="bg-gray-50 pb-4">
      <div class="leading-4 pt-responsive">
        <p
          class="text-3xl font-semibold text-shadow"
          v-html="t('employees.list.title', 1)" />
        <p
          class="text-gray-500 text-shadow"
          v-html="t('employees.list.description')" />
      </div>
      <div class="sm:flex justify-between pt-8">
        <div class="flex mb-3 sm:mb-0 max-w-2xl sm:w-2/3 md:w-1/2 lg:w-5/12">
          <DTextField
            :placeholder="t('form.button.search') + '...'"
            :icon="'SearchLgIcon'"
            class="mx-4 w-full"
            v-model="search"
            @keyup.enter="employees" />

          <DBtn @click.prevent="employees" v-html="t('form.button.search')" />
          <DToggleField
            class="mr-2 flex-shrink-0 mt-2 ml-4"
            :label="t('form.button.includeInactive')"
            data-include-inactive-toggle
            v-model="includeInactive"
            @change="employees" />
        </div>
        <div class="flex gap-2">
          <DBtn
            color="excel"
            type="button"
            @click="downloadList"
            v-html="t('form.button.download')" />
          <DBtn
            :icon="'PlusIcon'"
            type="button"
            @click="createEmployee()"
            v-if="store.hasPermission('employee:create')">
            {{
              t('form.button.create', { form: t('employees.list.title', 0) })
            }}
          </DBtn>
        </div>
      </div>
    </div>
    <DTable
      ref="tableEmployee"
      :columns="columns"
      :rows="rows"
      :total-rows="totalRows"
      :select-options="false"
      :text-per-page="t('table.rowsPerPage')"
      @perPage=";(perPage = $event), employees()"
      @offset="offset = $event"
      @sort=";(sortBy = $event), employees()">
      <template #actions="data: any">
        <div class="flex py-2.5 justify-end gap-1">
          <Popper
            v-if="store.hasPermission('employee:get')"
            arrow
            offsetDistance="12"
            :content="t('table.action.detail')"
            :hover="true"
            placement="top"
            class="tooltip"
            locked>
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="getEmployee(data.id)">
              <SearchMdIcon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
          <Popper
            v-if="
              store.hasPermission('employee_code_change_request:create') &&
              !data.leave_date
            "
            arrow
            offsetDistance="12"
            :content="
              t(
                `table.action.codeChangeRequest.${
                  data.codeChange?.toLowerCase() || 'default'
                }`
              )
            "
            :hover="true"
            placement="top"
            class="tooltip"
            locked>
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="changeCodeRequest(data)">
              <UsersEditIcon
                v-if="!data.codeChange || data.codeChange == 'APPROVED'"
                class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />

              <AlertTriangleIcon
                v-if="data.codeChange == 'REJECTED'"
                class="h-6 w-6 mx-2 cursor-pointer text-red-600" />
              <Loading02Icon
                v-if="data.codeChange == 'PENDING'"
                class="h-6 w-6 mx-2 cursor-pointer text-blue-600 animate-spin" />
            </div>
          </Popper>
          <Popper
            v-if="
              store.hasPermission('employee:create-reentry-request') &&
              data.leave_date &&
              !employeesWithReentry.has(data.id)
            "
            arrow
            offsetDistance="12"
            :content="t('table.action.reentryRequest')"
            :hover="true"
            placement="top"
            class="tooltip"
            locked>
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="reentryRequest(data)">
              <RouteIcon class="h-6 w-6 mx-2 cursor-pointer text-blue-600" />
            </div>
          </Popper>
          <Popper
            v-if="store.hasPermission('employee:update')"
            arrow
            offsetDistance="12"
            :content="t('table.action.edit')"
            :hover="true"
            placement="top"
            class="tooltip"
            locked>
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="updateEmployee(data.id)">
              <Edit03Icon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>

          <Popper
            v-if="store.hasPermission('employee:delete')"
            arrow
            offsetDistance="12"
            :content="t('table.action.remove')"
            :hover="true"
            placement="top"
            class="tooltip"
            locked>
            <div class="bg-gray-50 rounded-md py-2" @click="removeEntity(data)">
              <Trash03Icon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
        </div>
      </template>
      <template #noDataContent>
        <div
          class="text-center py-8"
          v-if="rows.length === 0 && !includeInactive">
          <h3 class="text-lg font-medium text-gray-900 mb-2">
            {{ t('employees.list.noActiveEmployees') }}
          </h3>
          <p class="text-gray-600 font-normal mb-4">
            {{ t('employees.list.activateInactiveToSee') }}
          </p>
          <DBtn @click="activateIncludeInactive" class="font-normal" color="primary">
            {{ t('form.button.includeInactive') }}
          </DBtn>
        </div>
        <div class="text-center py-8" v-else-if="rows.length === 0">
          <h3 class="text-lg font-medium text-gray-900 mb-2">
            {{ t('employees.list.noActiveEmployees') }}
          </h3>
          <p class="text-gray-600 font-normal mb-4">
            {{ t('employees.list.noDataDescription') }}
          </p>
          <DBtn color="primary" type="button" @click="createEmployee" class="font-normal">
            {{
              t('form.button.create', { form: t('employees.list.title', 0) })
            }}
          </DBtn>
        </div>
      </template>
    </DTable>
  </div>
</template>
